import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isEmpty } from "lodash";
import { Box, H5, Divider } from "orcs-design-system";

import GroupsNavigationBase from "src/components/GroupsNavigation/GroupsNavigation";
import GroupsTreeHeader from "src/components/GroupsNavigation/GroupsTreeHeader";
import LoadingBlock from "src/components/GroupsNavigation/LoadingBlock";
import useGroupsTreeData from "src/components/GroupsNavigation/useGroupsTreeData";
import useGroupTreeEventListeners from "src/components/GroupsNavigation/useGroupTreeEventListeners";
import useUpdateCurrentGroupListener from "src/components/GroupsNavigation/useUpdateCurrentGroupListener";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import {
  DEFAULT_DIVISIONS_LABEL,
  DIVISIONS_IN_EXPLORE_LABEL_FLAG,
} from "src/consts/divisions";
import { getDivisionRootGroupsQuery } from "src/queries/group.graphql";
import { teamIdFromUrl } from "src/consts/urlPaths";

// Cast to bypass exactOptionalPropertyTypes on optional props (same pattern
// as the GroupsNavigation loader).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GroupsNavigation = GroupsNavigationBase as any;

// The division root query takes no variables and always runs while mounted
// (ExploreMenu already gates it behind the enableDivisionsInExplore flag).
const DIVISION_QUERY_VARIABLES = {};

type TreeHeaderNode = React.ComponentProps<typeof GroupsTreeHeader>["node"];

/**
 * Explore menu section for the HRIS division hierarchy (kind=division
 * groups). Roots are fetched via the shared useGroupsTreeData hook (reusing its
 * Apollo cache-churn/dedupe guards); deeper levels lazy-load through the same
 * child-teams query used by the Teams tree, since divisions share the group
 * parent/child structure. Rendered only when the enableDivisionsInExplore
 * workspace feature flag is on.
 */
const DivisionsNavigation = () => {
  const workspace = useWorkspace();
  const featureFlags = useWorkspaceFeatureFlags();
  const history = useHistory();
  const location = useLocation();
  const activeNodeId = teamIdFromUrl(location.pathname);

  const configuredLabel = featureFlags?.[DIVISIONS_IN_EXPLORE_LABEL_FLAG];
  const divisionsLabel =
    (typeof configuredLabel === "string" && configuredLabel.trim()) ||
    DEFAULT_DIVISIONS_LABEL;

  const { root, treeData, rootGroupsLoading, groupTypes, sortOption } =
    useGroupsTreeData({
      workspace,
      featureFlags,
      isSelected: true,
      query: getDivisionRootGroupsQuery,
      variables: DIVISION_QUERY_VARIABLES,
      skip: false,
      // Degrade gracefully (hide the section) if the API doesn't support the
      // query yet, rather than throwing and breaking the Explore menu.
      throwOnError: false,
    });

  const [divisionsTree, setDivisionsTree] = useState<object | unknown[]>([]);
  const [hierarchyIds, setHierarchyIds] = useState<string[] | null>(null);

  const refreshTree = useCallback(() => {
    setDivisionsTree((tree) =>
      Array.isArray(tree) ? [...tree] : { ...(tree as object) }
    );
  }, []);

  // Keep the tree in sync with team/division mutations made elsewhere
  // (add/rename/remove), same as the Teams tree loader.
  useGroupTreeEventListeners(root, groupTypes, refreshTree);

  // Track the active group's hierarchy so the tree expands/lazy-loads to reveal
  // the current division on deep-link, refresh, or navigation from elsewhere —
  // same active-path sync the Teams tree gets via useGroupsNavigationData.
  useUpdateCurrentGroupListener(root, setHierarchyIds, refreshTree);

  useEffect(() => {
    setDivisionsTree(treeData ?? []);
  }, [treeData]);

  const navigateTo = useCallback(
    (pathname: string, groupPathIds: string[]) => {
      setHierarchyIds(groupPathIds);
      history.push(pathname);
    },
    [history]
  );

  // Division types ("Division - Level N") are generated on import and are not
  // part of the workspace team type config, so skip the type badge rendered
  // for regular teams.
  const customizeTreeHeader = useCallback(
    ({ node }: { node: TreeHeaderNode }) => <GroupsTreeHeader node={node} />,
    []
  );

  if (rootGroupsLoading) {
    return <LoadingBlock />;
  }

  // Gate on the local tree that is actually rendered (not the hook's treeData)
  // so we never briefly render GroupsNavigation with an empty list — which would
  // flash its hardcoded "No teams data available" copy under the Divisions
  // heading before the local state syncs. Empty response / unsupported-API error
  // (errorPolicy "all") leaves this empty, so the section stays hidden.
  if (isEmpty(divisionsTree)) {
    return null;
  }

  return (
    <Box data-testid="divisions-navigation">
      <H5 fontWeight="bold">{divisionsLabel}</H5>
      <Box my="s">
        <Divider light />
      </Box>
      <GroupsNavigation
        root={root as never}
        treeData={divisionsTree as never}
        navigateTo={navigateTo}
        refreshTree={refreshTree}
        groupTypes={groupTypes}
        sortOption={sortOption}
        customizeTreeHeader={customizeTreeHeader}
        {...(hierarchyIds != null ? { hierarchyIds } : {})}
        {...(activeNodeId != null ? { activeNodeId } : {})}
      />
    </Box>
  );
};

export default DivisionsNavigation;
